import { useCallback, useRef, useState } from "react";

/**
 * A minimal touch pull-to-refresh for the mobile shell content ("pull-to-refresh"). Fires
 * `onRefresh` when the user drags down from the top of the scroll container past `threshold`, and exposes the
 * live pull distance so the shell can render the drag indicator. No-op without touch input (a narrow desktop
 * viewport just never triggers it), so it is safe in both the Capacitor build and the `viewport < md` browser.
 */
export function usePullToRefresh(onRefresh: () => Promise<void> | void, threshold = 64) {
  const [distance, setDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startY = useRef<number | null>(null);

  const onTouchStart = useCallback(
    (e: React.TouchEvent<HTMLElement>) => {
      if (refreshing) return;
      // Only arm the gesture when the content is scrolled to the very top, and only for a touch that is really ON
      // the content. React bubbles events through the COMPONENT tree, so a touch inside a portalled overlay opened
      // from a page (a sheet, a modal) reaches these handlers too — a drag down in the New opportunity sheet grew
      // the pull gap and slid the page down behind it (ART-251). The DOM test excludes every portal.
      const onContent = e.target instanceof Node && e.currentTarget.contains(e.target);
      startY.current = onContent && e.currentTarget.scrollTop <= 0 ? e.touches[0].clientY : null;
    },
    [refreshing],
  );

  const onTouchMove = useCallback(
    (e: React.TouchEvent<HTMLElement>) => {
      if (startY.current === null || refreshing) return;
      const delta = e.touches[0].clientY - startY.current;
      if (delta > 0) setDistance(Math.min(delta * 0.5, threshold * 1.5)); // rubber-band, capped
    },
    [refreshing, threshold],
  );

  const onTouchEnd = useCallback(async () => {
    if (startY.current === null) return;
    startY.current = null;
    if (distance >= threshold) {
      setRefreshing(true);
      try {
        await onRefresh();
      } finally {
        setRefreshing(false);
      }
    }
    setDistance(0);
  }, [distance, threshold, onRefresh]);

  return { distance, refreshing, handlers: { onTouchStart, onTouchMove, onTouchEnd } };
}
